import L from "leaflet";
import type { CrowdGuessAggregate, LatLng } from "../types";
import { feedback } from "../services/feedback";
import { kmToMiles } from "../lib/format";

// Rough lat-lng → continent buckets. Used only for the "Europe" / "Asia"
// flash-tooltip on guess; the server is the source of truth for scoring, so
// these can be approximate. Order matters — first match wins.
function roughContinent(lat: number, lng: number): string | null {
  if (lat < -60) return "Antarctica";
  if (lng >= 110 && lng <= 180 && lat >= -50 && lat <= 0) return "Oceania";
  if (lng >= -82 && lng <= -34 && lat >= -56 && lat <= 13) return "South America";
  if (lng >= -170 && lng <= -50 && lat >= 7 && lat <= 83) return "North America";
  if (lng >= -25 && lng <= 60 && lat >= 35 && lat <= 72) return "Europe";
  if (lng >= -18 && lng <= 52 && lat >= -35 && lat <= 38) return "Africa";
  if (lng >= 26 && lng <= 180 && lat >= -10 && lat <= 78) return "Asia";
  return null;
}

// Carto's "voyager" basemap renders place names in Latin script (English where available),
// e.g. "Tokyo" not "東京", "Beijing" not "北京". No API key required, free with attribution.
const TILE_LAYER_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
// "voyager_nolabels" keeps the same coastline / terrain treatment but drops
// every place-name label — used by Iron Cartographer (hard mode).
const TILE_LAYER_NOLABELS_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png";

// Default view: mid-Atlantic, framed so North America and Europe are both
// visible at zoom 2. Restored after every round for consistency.
const DEFAULT_CENTER: L.LatLngExpression = [40, -30];
const DEFAULT_ZOOM = 2;
const TILE_SUBDOMAINS = "abcd";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>';

export class GuessMap {
  private readonly map: L.Map;
  private guessMarker: L.CircleMarker | null = null;
  private answerLayer = L.layerGroup();
  private crowdLayer = L.layerGroup();
  private locked = false;
  private tileLayer: L.TileLayer;
  private labelsHidden = false;

  constructor(containerId: string, onGuess: (latLng: LatLng) => void) {
    // Map wraps horizontally — the player can scroll east or west forever
    // and the world repeats. We keep a vertical bound so they can't scroll
    // off the poles, but no horizontal lock.
    this.map = L.map(containerId, {
      zoomControl: false,
      minZoom: 2,
      maxZoom: 6,
      worldCopyJump: true,
      maxBounds: L.latLngBounds(L.latLng(-85, -Infinity), L.latLng(85, Infinity)),
      maxBoundsViscosity: 0
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    this.tileLayer = L.tileLayer(TILE_LAYER_URL, {
      attribution: TILE_ATTRIBUTION,
      subdomains: TILE_SUBDOMAINS,
      noWrap: false,
      detectRetina: true,
      maxZoom: 10
    });
    this.tileLayer.addTo(this.map);
    L.control.zoom({ position: "bottomright" }).addTo(this.map);

    // Soft pitched click on every zoom-step transition.
    let lastZoom = this.map.getZoom();
    this.map.on("zoomend", () => {
      const z = this.map.getZoom();
      if (z === lastZoom) return;
      feedback.mapZoom(z > lastZoom ? "in" : "out");
      lastZoom = z;
    });
    this.crowdLayer.addTo(this.map);
    this.answerLayer.addTo(this.map);

    this.map.on("click", (event) => {
      if (this.locked) {
        return;
      }

      const latLng = { lat: event.latlng.lat, lng: event.latlng.lng };
      this.setGuess(latLng);
      feedback.pinDrop();
      onGuess(latLng);
    });
  }

  setLabelsHidden(hidden: boolean): void {
    if (hidden === this.labelsHidden) return;
    this.labelsHidden = hidden;
    this.map.removeLayer(this.tileLayer);
    this.tileLayer = L.tileLayer(hidden ? TILE_LAYER_NOLABELS_URL : TILE_LAYER_URL, {
      attribution: TILE_ATTRIBUTION,
      subdomains: TILE_SUBDOMAINS,
      noWrap: false,
      detectRetina: true,
      maxZoom: 10
    });
    this.tileLayer.addTo(this.map);
  }

  reset(): void {
    this.locked = false;
    this.map.stop();
    if (this.guessMarker) {
      this.map.removeLayer(this.guessMarker);
      this.guessMarker = null;
    }

    this.answerLayer.clearLayers();
    this.crowdLayer.clearLayers();
    this.map.setMaxZoom(6);
    this.restoreDefaultView();
    window.requestAnimationFrame(() => {
      this.map.invalidateSize({ pan: false });
      this.restoreDefaultView();
    });
  }

  showCrowdGuesses(aggregate: CrowdGuessAggregate): void {
    this.crowdLayer.clearLayers();
    if (aggregate.guesses.length === 0) {
      return;
    }

    const max = aggregate.guesses.reduce((peak, entry) => Math.max(peak, entry.count), 1);
    for (const entry of aggregate.guesses) {
      const intensity = Math.min(1, entry.count / Math.max(1, max));
      L.circleMarker([entry.lat, entry.lng], {
        radius: 4 + intensity * 14,
        weight: 0,
        fillColor: "#fbbf24",
        fillOpacity: 0.18 + intensity * 0.5,
        interactive: false
      }).addTo(this.crowdLayer);
    }
  }

  clearGuess(): void {
    if (this.guessMarker) {
      this.map.removeLayer(this.guessMarker);
      this.guessMarker = null;
    }
  }

  showAnswer(answer: LatLng, options: { dramatic?: boolean; onLineComplete?: () => void } = {}): void {
    this.locked = true;
    this.answerLayer.clearLayers();
    const guess = this.guessMarker?.getLatLng();
    const lineDurationMs = options.dramatic ? 3200 : 700;

    if (guess) {
      // Tighter close-up on close guesses so the player can see the exact spot
      // and how short the line was. Far guesses stay at a wider frame — the
      // line itself communicates the distance.
      const distanceKm = guess.distanceTo([answer.lat, answer.lng]) / 1000;
      const revealMaxZoom =
        distanceKm < 25 ? 10 :
        distanceKm < 100 ? 9 :
        distanceKm < 400 ? 8 :
        distanceKm < 1500 ? 7 : 6;
      this.map.setMaxZoom(revealMaxZoom);
      this.map.fitBounds(L.latLngBounds([guess, [answer.lat, answer.lng]]), {
        padding: [30, 30],
        maxZoom: revealMaxZoom,
        animate: true
      });
    } else {
      this.map.setView([answer.lat, answer.lng], 4, { animate: true });
    }

    // Pin pop-in: starts large + transparent, animates to full size.
    const answerMarker = L.circleMarker([answer.lat, answer.lng], {
      radius: 24,
      weight: 0,
      color: "#ffffff",
      fillColor: "#ff7a45",
      fillOpacity: 0
    }).addTo(this.answerLayer);

    const startedAt = performance.now();
    const popDurationMs = 480;
    const animatePin = () => {
      const t = Math.min(1, (performance.now() - startedAt) / popDurationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const radius = 24 - (24 - 8) * eased;
      const opacity = eased;
      answerMarker.setStyle({
        radius,
        weight: 2,
        fillOpacity: opacity,
        opacity
      });
      if (t < 1) {
        window.requestAnimationFrame(animatePin);
      }
    };
    window.requestAnimationFrame(animatePin);

    if (!guess) {
      return;
    }

    // Dashed polyline drawing animation: grows from guess to answer.
    const lineStartedAt = performance.now();
    const lat0 = guess.lat;
    const lng0 = guess.lng;
    const lat1 = answer.lat;
    const lng1 = answer.lng;
    const polyline = L.polyline([guess, guess], {
      color: "#000000",
      weight: 6,
      opacity: 0.95,
      dashArray: "8 8"
    }).addTo(this.answerLayer);

    // Pre-compute the great-circle distance for the floating label.
    const distanceKm = this.map.distance(guess, [answer.lat, answer.lng]) / 1000;
    const totalMiles = kmToMiles(distanceKm);
    const formatMiles = (mi: number): string => {
      if (mi >= 100) return `${Math.round(mi).toLocaleString()} mi`;
      if (mi >= 1) return `${mi.toFixed(1)} mi`;
      return `${(mi * 5280).toFixed(0)} ft`;
    };

    // Attach the live tooltip immediately and update it as the line grows so
    // the distance counts up alongside the animated draw.
    polyline
      .bindTooltip("0 mi", {
        permanent: true,
        direction: "center",
        className: "distance-label",
        offset: [0, 0]
      })
      .openTooltip();

    const animateLine = () => {
      const t = Math.min(1, (performance.now() - lineStartedAt) / lineDurationMs);
      const eased = 1 - Math.pow(1 - t, 2);
      const lat = lat0 + (lat1 - lat0) * eased;
      const lng = lng0 + (lng1 - lng0) * eased;
      polyline.setLatLngs([guess, [lat, lng]]);
      // Update the live distance counter as the line extends.
      polyline.setTooltipContent(formatMiles(totalMiles * eased));
      if (t < 1) {
        window.requestAnimationFrame(animateLine);
      } else {
        // Snap to exact final value to avoid floating-point dust.
        polyline.setTooltipContent(formatMiles(totalMiles));
        options.onLineComplete?.();
      }
    };
    window.requestAnimationFrame(animateLine);
  }

  // Drop the opponent's pin alongside the answer at reveal time. Used by
  // duel mode so the player sees where their friend actually landed —
  // the tactile satisfaction of "I beat them by 40 km" comes from
  // seeing both pins on the same view, not from a number alone.
  showOpponentPin(latLng: LatLng, label: string): void {
    const marker = L.circleMarker([latLng.lat, latLng.lng], {
      radius: 9,
      weight: 2,
      color: "#1e293b",
      fillColor: "#a78bfa",
      fillOpacity: 0.95
    }).addTo(this.answerLayer);
    marker.bindTooltip(label, {
      permanent: true,
      direction: "top",
      className: "opponent-pin-label",
      offset: [0, -6]
    }).openTooltip();
  }

  invalidateSize(): void {
    window.requestAnimationFrame(() => this.map.invalidateSize({ pan: false }));
  }

  private restoreDefaultView(): void {
    this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
  }

  private setGuess(latLng: LatLng): void {
    if (!this.guessMarker) {
      this.guessMarker = L.circleMarker([latLng.lat, latLng.lng], {
        radius: 18,
        weight: 2,
        color: "#0f172a",
        fillColor: "#7dd3fc",
        fillOpacity: 0
      }).addTo(this.map);
    } else {
      this.guessMarker.setLatLng([latLng.lat, latLng.lng]);
    }
    this.animatePinIn();
    this.flashContinentLabel(latLng);
  }

  private animatePinIn(): void {
    const marker = this.guessMarker;
    if (!marker) return;
    const startedAt = performance.now();
    const startRadius = 18;
    const endRadius = 8;
    const tick = () => {
      // Bail if a different marker has been swapped in.
      if (this.guessMarker !== marker) return;
      const t = Math.min(1, (performance.now() - startedAt) / 320);
      // ease-out cubic — settles into its final shape, no overshoot
      const eased = 1 - Math.pow(1 - t, 3);
      const radius = startRadius + (endRadius - startRadius) * eased;
      marker.setStyle({ radius, fillOpacity: eased, opacity: eased });
      if (t < 1) {
        window.requestAnimationFrame(tick);
      }
    };
    window.requestAnimationFrame(tick);
  }

  private flashContinentLabel(latLng: LatLng): void {
    const marker = this.guessMarker;
    if (!marker) return;
    const continent = roughContinent(latLng.lat, latLng.lng);
    if (!continent) return;
    marker.unbindTooltip();
    marker.bindTooltip(continent, {
      permanent: true,
      direction: "top",
      className: "guess-continent-label",
      offset: [0, -10]
    }).openTooltip();
    window.setTimeout(() => {
      if (this.guessMarker === marker) marker.unbindTooltip();
    }, 1200);
  }
}
